<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  label: {
    type: Object,
    required: true,
  },
  action: {
    type: String,
    required: true,
    validator: value =>
      ['apply_to_contacts', 'remove_from_contacts'].includes(value),
  },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const preview = computed(
  () => getters['labelContactBulkOperations/getPreview'].value
);
const uiFlags = computed(
  () => getters['labelContactBulkOperations/getUIFlags'].value
);
const isApply = computed(() => props.action === 'apply_to_contacts');

const submitting = ref(false);

const modalTitle = computed(() =>
  isApply.value
    ? t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.APPLY_TITLE')
    : t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.REMOVE_TITLE')
);

const description = computed(() =>
  isApply.value
    ? t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.APPLY_DESCRIPTION')
    : t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.REMOVE_DESCRIPTION')
);

const confirmLabel = computed(() =>
  isApply.value
    ? t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.CONFIRM_APPLY')
    : t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.CONFIRM_REMOVE')
);

const totalConversations = computed(
  () => preview.value?.total_conversations ?? 0
);
const totalContacts = computed(() => preview.value?.total_contacts ?? 0);
const updatableContacts = computed(
  () => preview.value?.updatable_contacts ?? 0
);
const skippableContacts = computed(
  () => preview.value?.skippable_contacts ?? 0
);
const hasExternalConsumer = computed(
  () => preview.value?.has_external_consumer === true
);

const isConfirmDisabled = computed(
  () =>
    uiFlags.value.isPreviewing ||
    submitting.value ||
    updatableContacts.value === 0
);

const loadPreview = () => {
  store.dispatch('labelContactBulkOperations/preview', {
    labelId: props.label.id,
    action: props.action,
  });
};

const confirm = async () => {
  submitting.value = true;
  try {
    await store.dispatch('labelContactBulkOperations/create', {
      labelId: props.label.id,
      action: props.action,
    });
    useAlert(t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.TOAST_STARTED'));
    emit('close');
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.TOAST_GENERIC_ERROR');
    useAlert(message);
  } finally {
    submitting.value = false;
  }
};

onMounted(loadPreview);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="description"
    />

    <div class="px-8 py-4">
      <div class="mb-4">
        <span class="text-xs font-medium uppercase text-n-slate-11">
          {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.LABEL_LABEL') }}
        </span>
        <div class="flex items-center gap-2 mt-1">
          <span
            class="w-3 h-3 rounded-full"
            :style="{ backgroundColor: label.color }"
          />
          <span class="font-medium text-n-slate-12">{{ label.title }}</span>
        </div>
      </div>

      <div
        v-if="uiFlags.isPreviewing"
        class="flex items-center gap-2 py-6 text-sm text-n-slate-11"
      >
        <Spinner size="small" />
        <span>{{
          $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.LOADING_PREVIEW')
        }}</span>
      </div>

      <div v-else-if="preview" class="flex flex-col gap-2 text-sm">
        <div class="flex justify-between gap-3 py-2 border-b border-n-weak">
          <span class="text-n-slate-11">
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.STATS.CONVERSATIONS') }}
          </span>
          <span class="font-medium text-n-slate-12">{{
            totalConversations
          }}</span>
        </div>
        <div class="flex justify-between gap-3 py-2 border-b border-n-weak">
          <span class="text-n-slate-11">
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.STATS.CONTACTS') }}
          </span>
          <span class="font-medium text-n-slate-12">{{ totalContacts }}</span>
        </div>
        <div class="flex justify-between gap-3 py-2 border-b border-n-weak">
          <span class="text-n-slate-11">
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.STATS.UPDATABLE') }}
          </span>
          <span class="font-semibold text-n-teal-11">{{
            updatableContacts
          }}</span>
        </div>
        <div class="flex justify-between gap-3 py-2">
          <span class="text-n-slate-11">
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.STATS.SKIPPABLE') }}
          </span>
          <span class="font-medium text-n-slate-11">{{
            skippableContacts
          }}</span>
        </div>

        <div
          v-if="hasExternalConsumer"
          class="flex flex-col gap-1 p-3 mt-2 rounded border border-n-amber-7 bg-n-amber-2 text-n-amber-12"
        >
          <span class="flex items-center gap-2 font-medium text-sm">
            <span class="i-lucide-triangle-alert size-4" />
            {{
              $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.INTEGRATION_WARNING_TITLE')
            }}
          </span>
          <span class="text-xs">
            {{ $t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.INTEGRATION_WARNING') }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-end gap-2 px-8 py-4 border-t border-n-weak"
    >
      <NextButton
        faded
        slate
        :label="$t('LABEL_MGMT.BULK_CONTACT_OPS.MODAL.CANCEL')"
        @click="emit('close')"
      />
      <NextButton
        :label="confirmLabel"
        :is-loading="submitting"
        :disabled="isConfirmDisabled"
        @click="confirm"
      />
    </div>
  </div>
</template>
